import { useState } from 'react';
import { X } from 'lucide-react';
import { accessT, newDocsSummary } from './chatAccessI18n';
import { chatApi, errKey } from './chatAccessApi';
import { Card, ErrorText, IdentifyStep, OtpStep, btnCls, linkBtnCls } from './AccessSteps';
import { DocumentsView } from './DocumentsView';
import { AccessRequestForm } from './AccessRequestForm';

export const ClientAccessPanel = ({ sessionId, language, onClose, onSummary }) => {
  const t = accessT[language] || accessT.it;
  const [step, setStep] = useState('identify');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [fiscal, setFiscal] = useState('');
  const [email, setEmail] = useState('');
  const [auth, setAuth] = useState(null);
  const [data, setData] = useState(null);
  const [reqBody, setReqBody] = useState(null);

  const showErr = (key) => setError(key ? (t.errors[key] || t.errors.generic) : '');
  const run = async (fn) => {
    setBusy(true);
    showErr('');
    try { await fn(); } catch (err) {
      const k = errKey(err);
      showErr(typeof k === 'string' ? k : 'generic');
      if (k === 'session_expired') { setAuth(null); setStep('identify'); }
    } finally { setBusy(false); }
  };

  const identify = (value) => run(async () => {
    setFiscal(value);
    const r = await chatApi.identify({ fiscal_id: value, session_id: sessionId, language });
    if (r.found) { setEmail(r.masked_email); setStep('otp'); } else setStep('notfound');
  });
  const verify = (code) => run(async () => {
    const r = await chatApi.verify({ session_id: sessionId, code });
    const docs = await chatApi.documents(r.token);
    setData(docs);
    setAuth(r);
    setStep('docs');
    onSummary(newDocsSummary(docs, t, language));
  });
  const sendRequest = (body) => run(async () => {
    const r = await chatApi.request({ ...body, session_id: sessionId, language });
    setReqBody(body);
    setEmail(r.masked_email);
    setStep('request_otp');
  });
  const verifyRequest = (code) => run(async () => {
    await chatApi.verifyRequest({ session_id: sessionId, code });
    setStep('request_done');
  });

  const title = step.startsWith('request') ? t.reqTitle : t.identifyTitle;
  return (
    <Card title={<span className="flex-1 flex items-center justify-between">{title}<button onClick={onClose} aria-label="close" data-testid="chat-access-close"><X className="w-4 h-4 text-slate-400" /></button></span>} testId="chat-access-panel">
      {step === 'identify' && <IdentifyStep t={t} busy={busy} onSubmit={identify} />}
      {step === 'otp' && <OtpStep t={t} email={email} busy={busy} onSubmit={verify} onResend={() => identify(fiscal)} testPrefix="chat-access" />}
      {step === 'docs' && auth && data && (
        <DocumentsView data={data} token={auth.token} t={t} name={auth.name} error={error} onError={showErr} lang={language}
          onLogout={() => { setAuth(null); setData(null); setStep('identify'); }} />
      )}
      {step === 'notfound' && (
        <div className="space-y-2" data-testid="chat-access-notfound">
          <p className="text-xs">{t.notFound}</p>
          <button onClick={() => setStep('request')} className={btnCls} data-testid="chat-access-request-btn">{t.requestBtn}</button>
          <button onClick={() => setStep('identify')} className={linkBtnCls} data-testid="chat-access-retry">{t.retry}</button>
        </div>
      )}
      {step === 'request' && <AccessRequestForm t={t} lang={language} initialFiscal={fiscal} busy={busy} onSubmit={sendRequest} onCancel={() => setStep('identify')} />}
      {step === 'request_otp' && <OtpStep t={t} email={email} busy={busy} onSubmit={verifyRequest} onResend={() => sendRequest(reqBody)} testPrefix="chat-request" />}
      {step === 'request_done' && <p className="text-xs text-green-700" data-testid="chat-request-done">{t.reqDone}</p>}
      {step !== 'docs' && <ErrorText text={error} />}
    </Card>
  );
};
